import type { RecordLocationRef, RecordTableRow } from './RecordsList';

/**
 * One (week, Feed Mix) point of the delivered-feed chart, as served by
 * `Records/feed-deliveries/weekly`. Snake_case because it mirrors the wire verbatim.
 *
 * Read only by the Deliveries page, which bins it per Feed Mix through `useGroupedWeeklyAggregation` rather than
 * through the shared records aggregation, so unlike the table rows below it carries no reshaping to
 * `RecordRow`'s pig-shaped fields.
 */
export type FeedDeliveryWeeklyRow = {
    /** Farm-local Monday of the week this delivery total bins into, `yyyy-MM-dd`. Server-computed. */
    week_start: string;
    feed_mix_id: number;
    feed_mix_name: string;
    quantity: number;
};

/**
 * One system-registered delivery as served by `Records/feed-deliveries/approved`. Snake_case
 * because it mirrors the wire verbatim.
 */
export type ApprovedFeedDeliveryApiRow = {
    id: number;
    /** Farm-local date of the delivery. `yyyy-MM-dd`. */
    date: string;
    /** Farm-local Monday of the week this delivery bins into, `yyyy-MM-dd`. Server-computed. */
    week_start: string;
    supplier: string;
    feed_mix_id: number;
    feed_mix_name: string;
    /** Null when the matched Feed Mix has no category. */
    feed_mix_category_id: number | null;
    feed_mix_category_name: string | null;
    quantity: number;
    /** Null unless the caller holds the Feed Prices read right - resolved server-side. */
    price: number | null;
    energy: number | null;
    protein: number | null;
    phosphorus: number | null;
    /** Null when the delivery could not be matched to a farm/barn. */
    farm: RecordLocationRef | null;
    barn: RecordLocationRef | null;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/**
 * `ApprovedFeedDeliveryApiRow`, reshaped to the field names the shared records kit reads.
 *
 * `number` replaces `quantity` for the same reason `DeathRow` replaces `amount` with it - it is
 * the exact field name `RecordRow`'s aggregation (the week filter, `useRecordsPage`'s footer
 * totals) reads off every records page. `avg_weight`/`total_weight` carry no meaning for a feed
 * delivery and are always null; nothing here renders them, they exist only to satisfy the shape.
 *
 * `farm`/`barn` are declared required (though nullable) here, narrowing `RecordTableRow`'s own
 * optional pair - the Approved endpoint always sends the keys, just `null` when unmatched, unlike
 * a `from_*`/`to_*` chain which can be absent outright.
 */
export type ApprovedFeedDeliveryRow = RecordTableRow & {
    supplier: string;
    feed_mix_id: number;
    feed_mix_name: string;
    feed_mix_category_id: number | null;
    feed_mix_category_name: string | null;
    price: number | null;
    energy: number | null;
    protein: number | null;
    phosphorus: number | null;
    farm: RecordLocationRef | null;
    barn: RecordLocationRef | null;
    /** Full name of the user who created the registration. Null when the creator is missing, deleted/anonymized, or has no name. */
    created_by: string | null;
};

/** `ApprovedFeedDeliveryApiRow` as the shared records kit reads it. */
export function mapApprovedFeedDeliveryRow(raw: ApprovedFeedDeliveryApiRow): ApprovedFeedDeliveryRow {
    return {
        record_id: raw.id,
        date: raw.date,
        week_start: raw.week_start,
        number: raw.quantity,
        avg_weight: null,
        total_weight: null,
        supplier: raw.supplier,
        feed_mix_id: raw.feed_mix_id,
        feed_mix_name: raw.feed_mix_name,
        feed_mix_category_id: raw.feed_mix_category_id,
        feed_mix_category_name: raw.feed_mix_category_name,
        price: raw.price,
        energy: raw.energy,
        protein: raw.protein,
        phosphorus: raw.phosphorus,
        farm: raw.farm,
        barn: raw.barn,
        created_by: raw.created_by,
    };
}

/**
 * One raw supplier-reported delivery as served by `Records/feed-deliveries/original`, before it is
 * matched to a Feed Mix. Snake_case because it mirrors the wire verbatim.
 */
export type OriginalFeedDeliveryApiRow = {
    id: number;
    /** Supplier-reported date of the delivery. `yyyy-MM-dd`. */
    date: string;
    /** Farm-local Monday of the week this delivery bins into, `yyyy-MM-dd`. Server-computed. */
    week_start: string;
    supplier: string;
    /** The supplier's own product/feed name, as reported - not a resolved Feed Mix name. */
    material_name: string;
    material_number: string;
    quantity: number;
    /** The farm the delivery was matched to - "Gård" in the legacy Leverancedata table this mirrors. */
    farm_name: string;
};

/**
 * `OriginalFeedDeliveryApiRow`, reshaped to the field names the shared records kit reads. See
 * `mapApprovedFeedDeliveryRow` for why `number` replaces `quantity`. Column set mirrors the legacy
 * Leverancedata table: material name, material number, quantity, farm - besides date/supplier.
 */
export type OriginalFeedDeliveryRow = RecordTableRow & {
    supplier: string;
    material_name: string;
    material_number: string;
    farm_name: string;
};

/** `OriginalFeedDeliveryApiRow` as the shared records kit reads it. */
export function mapOriginalFeedDeliveryRow(raw: OriginalFeedDeliveryApiRow): OriginalFeedDeliveryRow {
    return {
        record_id: raw.id,
        date: raw.date,
        week_start: raw.week_start,
        number: raw.quantity,
        avg_weight: null,
        total_weight: null,
        supplier: raw.supplier,
        material_name: raw.material_name,
        material_number: raw.material_number,
        farm_name: raw.farm_name,
    };
}
